import dayjs from 'dayjs';
import {
  Avatar,
  Card,
  CardHeader,
  CardBody,
  Divider,
  CardFooter,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { Patient } from '@/patient-app-common/types/Patient';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';

interface ProfileProps {
  patient: Patient;
}
export function Profile({ patient }: ProfileProps) {
  const { t } = useTranslation();

  const {
    clinicalContact,
    email,
    firstName,
    lastName,
    maternityProgram,
    nonSurgicalProgram,
    phone,
    procedureName,
    surgicalDetails,
    timezone,
    username,
  } = patient;

  const { patientDates } = usePatientDates();

  const name = firstName ? `${firstName} ${lastName}` : username;

  const hospitalZone = dayjs().tz(timezone).format('z');

  let surgeryDateDisplay = `${dayjs(patientDates?.surgeryDate).format('LL')} (${hospitalZone})`;

  if (
    surgicalDetails?.surgeryTime &&
    surgicalDetails?.enrollmentType === 'auto_enrolled'
  ) {
    surgeryDateDisplay = `${dayjs(patientDates?.surgeryDate).format('LL')} ${surgicalDetails.surgeryTime}`;
  }

  const ITEMS = [
    {
      label: t('common.text.email'),
      value: email || t('common.text.not_provided'),
      icon: 'mdi:email-outline',
    },
    {
      label: t('common.text.phone'),
      value: phone || t('common.text.not_provided'),
      icon: 'bi:telephone',
    },
    {
      label: t('common.health_provider.general'),
      value: `${clinicalContact.firstName} ${clinicalContact.lastName}`,
      icon: 'wpf:medical-doctor',
    },
    {
      label: t('common.text.program_start_date'),
      value: `${dayjs(patientDates?.signupDate).format('LL')}`,
      icon: 'bi:calendar-event',
      hidden: !nonSurgicalProgram,
    },
    {
      label: t('common.text.preop_visit_date'),
      value: `${dayjs(patientDates?.preOpDate).format('LL')}`,
      icon: 'bi:calendar-event',
      hidden: _.isEmpty(patientDates?.preOpDate),
    },
    {
      label: t('common.text.procedure_date', { procedureName }),
      value: surgeryDateDisplay,
      icon: 'bi:calendar-event',
      hidden:
        nonSurgicalProgram ||
        maternityProgram ||
        _.isEmpty(patientDates?.surgeryDate),
    },
    {
      label: t('common.text.due_date'),
      value: surgeryDateDisplay,
      icon: 'bi:calendar-event',
      hidden: !maternityProgram || _.isEmpty(patientDates?.surgeryDate),
    },
    {
      label: t('common.text.location'),
      value: surgicalDetails?.surgerySiteLocation,
      icon: 'mdi:map-marker-outline',
      hidden: !surgicalDetails?.surgerySiteLocation,
    },
    {
      label: t('common.text.discharge_date'),
      value: `${dayjs(patientDates?.dischargeDate).format('LL')} (${hospitalZone})`,
      icon: 'mdi:home',
      hidden: _.isEmpty(patientDates?.dischargeDate),
    },
  ];

  return (
    <Card shadow="sm" className="bg-default-100" isBlurred>
      <CardHeader>
        <Avatar
          color="primary"
          icon={<Icon icon="mdi:folder-user" className="text-3xl" />}
          size="md"
        />
        <div className="flex flex-col ml-4">
          <h1 className="font-light text-xl">{name}</h1>
        </div>
      </CardHeader>
      <Divider />
      <CardBody className="space-y-4">
        {ITEMS.filter((item) => !item.hidden).map((item) => (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2">
            <Icon icon={item.icon} className="text-lg text-default-500" />
            <span className="text-default-600">{item.label}</span>
            <span className="text-default-800">{item.value}</span>
          </div>
        ))}
      </CardBody>
      {!_.isEmpty(patient.contacts) && (
        <>
          <Divider />
          <CardFooter>
            <div>
              <div className="flex items-center gap-2">
                <Icon
                  icon="ri:contacts-book-2-fill"
                  className="text-lg text-default-500"
                />
                <span className="text-default-600">
                  {t('common.text.caregivers')}
                </span>
              </div>
              {patient.contacts.map((contact) => (
                <div className="grid grid-cols-2 my-4 text-sm">
                  <p>
                    {contact.firstName} {contact.lastName}
                  </p>
                  <div className="flex flex-col">
                    {contact.email && (
                      <span className="text-default-800">{contact.email}</span>
                    )}
                    {contact.phone && (
                      <span className="text-default-800">{contact.phone}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardFooter>
        </>
      )}
    </Card>
  );
}
